import React, { createContext, useContext, useEffect, useState } from 'react'

export type Theme = 'light' | 'dark' | 'system'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
  isDark: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('app-theme') as Theme
    return saved || 'system'
  })

  const [isDark, setIsDark] = useState<boolean>(false)

  useEffect(() => {
    localStorage.setItem('app-theme', theme)

    const root = document.documentElement
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    function applyTheme() {
      const activeIsDark = theme === 'dark' || (theme === 'system' && mediaQuery.matches)
      setIsDark(activeIsDark)
      
      if (activeIsDark) {
        root.setAttribute('data-theme', 'dark')
      } else {
        root.removeAttribute('data-theme')
      }
    }

    applyTheme()

    // Listen for OS theme changes if 'system' is selected
    if (theme === 'system') {
      const listener = () => applyTheme()
      mediaQuery.addEventListener('change', listener)
      return () => mediaQuery.removeEventListener('change', listener)
    }
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    const isDarkOS = typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
    return {
      theme: 'system' as const,
      setTheme: () => {},
      isDark: Boolean(isDarkOS),
    }
  }
  return context
}
